import { api } from '@invisoutlet/shared/api';
import { useAuthContext } from '@invisoutlet/shared/context';
import { useAllDevices, useWebSocketStatus } from '@invisoutlet/shared/hooks';
import type { Building, Device, DeviceSensorPayload, Entity } from '@invisoutlet/shared/types';
import {
  aggregateSensors,
  DEFAULT_OCCUPIED_COLOR,
  DEFAULT_VACANT_COLOR,
  generateMockSensorData,
  type SensorAggregation,
} from '@invisoutlet/shared/utils';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DeviceSensorCard } from '../components/device-sensor-card.js';
import { EnterpriseLayout } from '../components/enterprise-layout.js';

export const UNASSIGNED_LEVEL_KEY = '__default__';

export function computeLevels(
  building: Building | undefined,
  devices: Device[],
  buildingId: string | undefined,
): [string, string][] {
  const defined = Object.entries(building?.levelEnum ?? {});
  const hasUnassigned = devices.some((d) => d.building === buildingId && !d.levelEnum);
  if (!hasUnassigned) return defined;
  const defaultEntry: [string, string] = [UNASSIGNED_LEVEL_KEY, '(Default)'];
  return [...defined, defaultEntry];
}

export function filterFloorDevices(
  devices: Device[],
  buildingId: string | undefined,
  activeLevelKey: string,
): Device[] {
  return devices.filter((d) => {
    if (!d.deviceId) return false;
    if (d.building !== buildingId) return false;
    if (!activeLevelKey) return true;
    if (activeLevelKey === UNASSIGNED_LEVEL_KEY) return !d.levelEnum;
    return d.levelEnum === activeLevelKey;
  });
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface DeviceWithSensor {
  device: Device;
  sensor: DeviceSensorPayload | null;
}

// ─── Skeletons and empty states ───────────────────────────────────────────────

function FloorSkeleton() {
  return (
    <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {[1, 2, 3].map((g) => (
        <div key={g} className="animate-pulse rounded-xl border border-border bg-bg-primary p-4">
          <div className="mb-2 flex items-start justify-between">
            <div className="space-y-1">
              <div className="h-4 w-24 rounded bg-bg-elevated" />
              <div className="h-3 w-16 rounded bg-bg-elevated" />
            </div>
            <div className="h-7 w-16 rounded-lg bg-bg-elevated" />
          </div>
          <div className="mt-3 flex gap-4">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="h-3 w-16 rounded bg-bg-elevated" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyFloor() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-bg-elevated">
        <svg
          className="h-8 w-8 text-text-secondary"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M9 3v18M3 9h18M3 15h18" />
        </svg>
      </div>
      <h2 className="mb-2 text-lg font-semibold text-text-primary">No devices on this floor</h2>
      <p className="max-w-sm text-sm text-text-secondary">
        Devices will appear here once they have been provisioned and assigned to this floor.
      </p>
    </div>
  );
}

// ─── RoomCard ─────────────────────────────────────────────────────────────────

interface RoomCardProps {
  room: string;
  items: DeviceWithSensor[];
  temperatureUnit: '°F' | '°C';
  selfService?: boolean;
  occupiedColor?: string;
  vacantColor?: string;
}

function RoomCard({
  room,
  items,
  temperatureUnit,
  selfService,
  occupiedColor,
  vacantColor,
}: RoomCardProps) {
  const [expanded, setExpanded] = useState(false);
  const readySensors = items.filter((i) => i.sensor !== null);
  const pendingCount = items.length - readySensors.length;
  const agg: SensorAggregation = aggregateSensors(
    items.map((i) => i.sensor),
    temperatureUnit,
  );
  const roomId = useMemo(() => room.replace(/[^a-zA-Z0-9_-]/g, '-').toLowerCase(), [room]);

  const occupancyColor = agg.isOccupied
    ? (occupiedColor ?? DEFAULT_OCCUPIED_COLOR)
    : (vacantColor ?? DEFAULT_VACANT_COLOR);

  return (
    <section
      className={expanded ? 'sm:col-span-2 xl:col-span-3' : ''}
      aria-labelledby={`room-heading-${roomId}`}
    >
      <div
        className="rounded-xl border p-4"
        style={{
          backgroundColor: `${occupancyColor}14`,
          borderColor: `${occupancyColor}59`,
        }}
      >
        {/* Header row */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id={`room-heading-${roomId}`} className="text-sm font-semibold text-text-primary">
              {room}
            </h2>
            <p className="text-xs text-text-secondary">
              {items.length} device{items.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-controls={`room-devices-${roomId}`}
            className="flex shrink-0 items-center gap-1 rounded-lg border border-border bg-bg-surface px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
          >
            <svg
              className={`h-3.5 w-3.5 transition-transform duration-150 ${expanded ? 'rotate-90' : 'rotate-0'}`}
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 4l4 4-4 4" />
            </svg>
            {expanded ? 'Hide' : 'Show'}
          </button>
        </div>

        {/* Aggregated stats */}
        {pendingCount > 0 && readySensors.length === 0 ? (
          <div className="mt-3 flex items-center gap-3" aria-busy="true">
            <div className="h-3 w-14 animate-pulse rounded bg-bg-elevated" />
            <div className="h-3 w-14 animate-pulse rounded bg-bg-elevated" />
            <div className="h-3 w-14 animate-pulse rounded bg-bg-elevated" />
            <span className="text-xs text-text-secondary">Initializing…</span>
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-text-secondary">
            <span className="font-medium text-text-primary">{agg.statusDisplay}</span>
            <span>🌡 {agg.tempDisplay}</span>
            <span>💧 {agg.humidityDisplay}</span>
            <span>☀ {agg.luxDisplay}</span>
            <span>Outlets: {agg.outletsDisplay}</span>
            <span>Occupied: {agg.occupancyDisplay}</span>
            {pendingCount > 0 && (
              <span className="text-text-secondary opacity-60">{pendingCount} initializing</span>
            )}
            <span className="ml-auto">{agg.lastUpdatedDisplay}</span>
          </div>
        )}
      </div>

      {/* Expanded device grid */}
      {expanded && (
        <div
          id={`room-devices-${roomId}`}
          className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          {items.map(({ device }) => (
            <DeviceSensorCard
              key={device.deviceId}
              device={device}
              temperatureUnit={temperatureUnit}
              selfService={selfService}
              occupiedColor={occupiedColor}
              vacantColor={vacantColor}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function FloorViewPage() {
  const { buildingId } = useParams<{ buildingId: string }>();
  const { user } = useAuthContext();
  const entityId = user!.entityId!;

  // Entity data — shared React Query cache with Building Overview
  const { data: entityRes } = useQuery({
    queryKey: ['entity', entityId],
    queryFn: () => api.get<Entity>(`/api/entities/${entityId}`),
  });
  const entity = entityRes?.data;
  const building = entity?.buildings.find((b) => b.id === buildingId);

  const [selectedLevelKey, setSelectedLevelKey] = useState('');

  // Fetch all devices for this entity — React Query caches this across pages
  const { data: devicesRes, isLoading: devicesLoading } = useQuery({
    queryKey: ['devices', entityId],
    queryFn: () => api.get<Device[]>(`/api/entities/${entityId}/devices`),
    enabled: !!entityId,
  });

  const levels = useMemo(
    () => computeLevels(building, devicesRes?.data ?? [], buildingId),
    [building, devicesRes, buildingId],
  );

  const activeLevelKey = selectedLevelKey || levels[0]?.[0] || '';

  // Live sensor data from WebSocket store (empty map when not connected)
  const liveDevices = useAllDevices();
  const { connected } = useWebSocketStatus();

  const temperatureUnit = entity?.preference.temperatureUnit ?? '°F';
  const occupiedColor = entity?.preference.occupiedColor ?? DEFAULT_OCCUPIED_COLOR;
  const vacantColor = entity?.preference.vacantColor ?? DEFAULT_VACANT_COLOR;

  // Filter to this building + active level, group by room, enrich with sensor data
  const roomGroups = useMemo(() => {
    const devices = devicesRes?.data ?? [];
    const filtered = filterFloorDevices(devices, buildingId, activeLevelKey);

    const groups = new Map<string, DeviceWithSensor[]>();
    for (const device of filtered) {
      const room = device.room || 'Unassigned';
      const sensor =
        liveDevices[device.deviceId] ??
        (connected ? null : generateMockSensorData(device.deviceId));
      const existing = groups.get(room) ?? [];
      groups.set(room, [...existing, { device, sensor }]);
    }

    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [devicesRes, buildingId, activeLevelKey, liveDevices, connected]);

  const buildingName = building?.name ?? buildingId ?? 'Building';
  const activeLevelName =
    activeLevelKey === UNASSIGNED_LEVEL_KEY
      ? '(Default)'
      : (building?.levelEnum[activeLevelKey] ?? activeLevelKey);

  return (
    <EnterpriseLayout title={buildingName}>
      <div className="mx-auto max-w-7xl">
        {/* Breadcrumb */}
        <nav className="mb-5 flex items-center gap-2 text-sm" aria-label="Breadcrumb">
          <Link
            to="/"
            className="shrink-0 text-text-secondary transition-colors hover:text-text-primary"
          >
            Buildings
          </Link>
          <span className="shrink-0 text-text-secondary" aria-hidden="true">
            /
          </span>
          <span className="min-w-0 truncate font-medium text-text-primary">{buildingName}</span>
          {activeLevelName && (
            <>
              <span className="shrink-0 text-text-secondary" aria-hidden="true">
                /
              </span>
              <span className="shrink-0 text-text-secondary">{activeLevelName}</span>
            </>
          )}
        </nav>

        {/* Level tabs — only shown when there are multiple levels */}
        {levels.length > 1 && (
          <div
            className="mb-6 flex gap-1 overflow-x-auto border-b border-border"
            role="tablist"
            aria-label="Floor levels"
          >
            {levels.map(([key, name]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeLevelKey === key}
                onClick={() => setSelectedLevelKey(key)}
                className={[
                  '-mb-px shrink-0 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                  'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent',
                  activeLevelKey === key
                    ? 'border-accent text-accent'
                    : 'border-transparent text-text-secondary hover:border-border hover:text-text-primary',
                ].join(' ')}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        {devicesLoading ? (
          <FloorSkeleton />
        ) : roomGroups.length === 0 ? (
          <EmptyFloor />
        ) : (
          <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {roomGroups.map(([room, items]) => (
              <RoomCard
                key={room}
                room={room}
                items={items}
                temperatureUnit={temperatureUnit}
                selfService={user?.selfService}
                occupiedColor={occupiedColor}
                vacantColor={vacantColor}
              />
            ))}
          </div>
        )}
      </div>
    </EnterpriseLayout>
  );
}
